import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPut, apiPost } from '../../lib/api';
import { Building2, Plus, Save, Search, Users, ArrowRightLeft, Shield, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

function Pill({ color, children, onClick, active }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider font-display font-bold border transition-all ${active ? 'shadow-sm' : 'opacity-50 hover:opacity-100'}`}
      style={active ? { background: color, color: 'white', borderColor: color } : { background: 'white', color: color, borderColor: color }}
    >
      {children}
    </button>
  );
}

export default function CompanyManagement() {
  const [tab, setTab] = useState('companies');
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [savingUserId, setSavingUserId] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [c, u] = await Promise.all([
        apiGet('/companies/all'),
        apiGet('/companies/users'),
      ]);
      setCompanies(c);
      setUsers(u);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Load failed');
    }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const companyById = useMemo(() => {
    const m = {};
    companies.forEach(c => { m[c.id] = c; });
    return m;
  }, [companies]);

  const filteredUsers = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter(u =>
      (u.email || '').toLowerCase().includes(needle) ||
      (u.name || '').toLowerCase().includes(needle) ||
      (u.role || '').toLowerCase().includes(needle) ||
      (u.department || '').toLowerCase().includes(needle)
    );
  }, [users, q]);

  const updateUserAccess = async (u, payroll_company_id, accessible_company_ids) => {
    setSavingUserId(u.user_id);
    try {
      const r = await apiPut(`/companies/users/${u.user_id}/access`, {
        payroll_company_id, accessible_company_ids,
      });
      setUsers(prev => prev.map(x => x.user_id === u.user_id ? { ...x, ...r } : x));
      toast.success(`${u.email} access updated`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    }
    setSavingUserId(null);
  };

  const toggleAccess = (u, cid) => {
    const current = u.accessible_company_ids || [];
    const next = current.includes(cid)
      ? current.filter(x => x !== cid)
      : [...current, cid];
    // Ensure payroll company is always in accessible list
    const finalAccessible = Array.from(new Set([...next, u.payroll_company_id])).filter(Boolean);
    updateUserAccess(u, u.payroll_company_id, finalAccessible);
  };

  const setPayroll = (u, cid) => {
    const accessible = Array.from(new Set([...(u.accessible_company_ids || []), cid])).filter(Boolean);
    updateUserAccess(u, cid, accessible);
  };

  return (
    <div data-testid="company-management">
      <PageHeader
        overline="Settings · Multi-Company"
        title="Company Management"
        subtitle="Manage legal entities and grant cross-company access to users."
      />

      <div className="border-b border-slate-200 mb-5 flex gap-1">
        {[
          { k: 'companies', l: 'Companies', icon: Building2 },
          { k: 'users', l: 'User Access', icon: Users },
          { k: 'transfer', l: 'Employee Transfer', icon: ArrowRightLeft },
        ].map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`cm-tab-${t.k}`}
            className={`px-4 py-2.5 text-xs uppercase tracking-wider font-display font-bold border-b-2 transition-colors flex items-center gap-2 ${
              tab === t.k ? 'border-[#1F7A6E] text-[#1F7A6E]' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <t.icon size={13} /> {t.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="animate-spin text-[#1F7A6E] inline" size={20} /></div>
      ) : tab === 'companies' ? (
        <CompaniesTab companies={companies} onChange={refresh} />
      ) : tab === 'users' ? (
        <UsersTab
          users={filteredUsers}
          companies={companies}
          companyById={companyById}
          q={q} setQ={setQ}
          savingUserId={savingUserId}
          toggleAccess={toggleAccess}
          setPayroll={setPayroll}
        />
      ) : (
        <TransferTab companies={companies} onDone={refresh} />
      )}
    </div>
  );
}

function CompaniesTab({ companies, onChange }) {
  const [editing, setEditing] = useState(null);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {companies.map(c => (
        <CompanyCard key={c.id} company={c} onEdit={() => setEditing(c)} />
      ))}
      {editing && <CompanyEditor company={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChange(); }} />}
    </div>
  );
}

function CompanyCard({ company, onEdit }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid={`company-card-${company.short_key || company.id}`}>
      <div className="px-5 py-3 flex items-center gap-3" style={{ background: company.primary_color || '#1F7A6E', color: 'white' }}>
        <div className="w-9 h-9 rounded-sm bg-white/15 flex items-center justify-center"><Building2 size={16} /></div>
        <div className="flex-1">
          <div className="font-display font-bold">{company.name}</div>
          <div className="text-[10px] opacity-80">{company.legal_name}</div>
        </div>
        {company.is_parent && <span className="px-2 py-0.5 text-[9px] bg-white/20 rounded-sm uppercase tracking-wider font-bold">Parent</span>}
      </div>
      <div className="p-5 space-y-2 text-xs">
        <div className="grid grid-cols-2 gap-2">
          <div><span className="kpi-overline">Entity</span><div className="text-slate-700 font-medium">{company.entity_type}</div></div>
          <div><span className="kpi-overline">Status</span><div className="text-slate-700 font-medium">{company.status}</div></div>
          <div><span className="kpi-overline">GST</span><div className="text-slate-700 font-mono text-[11px]">{company.gst_number || '—'}</div></div>
          <div><span className="kpi-overline">PAN</span><div className="text-slate-700 font-mono text-[11px]">{company.pan || '—'}</div></div>
        </div>
        <button
          onClick={onEdit}
          data-testid={`edit-company-${company.short_key || company.id}`}
          className="mt-3 w-full px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs uppercase tracking-wider font-display font-bold rounded-sm"
        >
          Edit Details
        </button>
      </div>
    </div>
  );
}

function CompanyEditor({ company, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: company.name || '',
    legal_name: company.legal_name || '',
    entity_type: company.entity_type || 'Private Limited',
    gst_number: company.gst_number || '',
    pan: company.pan || '',
    cin: company.cin || '',
    registered_address: company.registered_address || '',
    bank_name: company.bank_name || '',
    bank_account_no: company.bank_account_no || '',
    bank_ifsc: company.bank_ifsc || '',
    primary_color: company.primary_color || '#1F7A6E',
    status: company.status || 'Active',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await apiPut(`/companies/${company.id}`, form);
      toast.success(`${form.name} saved`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white border border-slate-200 rounded-sm shadow-xl max-w-2xl w-full max-h-[85vh] overflow-auto" onClick={e => e.stopPropagation()} data-testid="company-editor">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="font-display font-bold text-sm">Edit {company.name}</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ['Display Name', 'name'],
            ['Legal Name', 'legal_name'],
            ['Entity Type', 'entity_type'],
            ['GST Number', 'gst_number'],
            ['PAN', 'pan'],
            ['CIN', 'cin'],
            ['Bank Name', 'bank_name'],
            ['Bank Account No.', 'bank_account_no'],
            ['Bank IFSC', 'bank_ifsc'],
            ['Primary Color', 'primary_color'],
          ].map(([label, key]) => (
            <div key={key}>
              <label className="kpi-overline mb-1 block">{label}</label>
              <input
                type={key === 'primary_color' ? 'color' : 'text'}
                value={form[key]}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                data-testid={`company-field-${key}`}
              />
            </div>
          ))}
          <div className="md:col-span-2">
            <label className="kpi-overline mb-1 block">Registered Address</label>
            <textarea
              value={form.registered_address}
              onChange={e => setForm(f => ({ ...f, registered_address: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
              rows={2}
            />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-wider font-display font-bold text-slate-500 hover:text-slate-700">Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="save-company-btn"
            className="inline-flex items-center gap-2 px-5 py-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs uppercase tracking-wider font-display font-bold rounded-sm disabled:opacity-50"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function UsersTab({ users, companies, companyById, q, setQ, savingUserId, toggleAccess, setPayroll }) {
  return (
    <div>
      <div className="bg-teal-50 border border-teal-200 rounded-sm p-3 text-xs text-teal-900 mb-4 flex items-start gap-2">
        <Shield size={13} className="mt-0.5 shrink-0" />
        <div>
          <b>Payroll Company</b> is where the user's salary is processed and where they punch attendance — set once per user.
          <b className="ml-2">Accessible Companies</b> is the list of entities whose operational data the user can view/edit.
          Grant additional access here for users in shared-services teams (HR, Finance, BD, Purchase).
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-3 mb-4 flex items-center gap-2">
        <Search size={14} className="text-slate-400" />
        <input
          type="text"
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search by name, email, role or department…"
          className="flex-1 text-sm outline-none"
          data-testid="user-search-input"
        />
        <span className="text-[10px] text-slate-500 ml-2">{users.length} users</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#1c2a4d] text-white">
              <tr>
                <th className="px-4 py-2.5 text-left text-[11px] uppercase tracking-wider font-display">User</th>
                <th className="px-4 py-2.5 text-left text-[11px] uppercase tracking-wider font-display">Role / Dept</th>
                <th className="px-4 py-2.5 text-left text-[11px] uppercase tracking-wider font-display">Payroll Co.</th>
                <th className="px-4 py-2.5 text-left text-[11px] uppercase tracking-wider font-display">Accessible Companies</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.user_id} className="border-b border-slate-100 hover:bg-teal-50/30" data-testid={`user-row-${u.user_id}`}>
                  <td className="px-4 py-2.5">
                    <div className="text-slate-800 font-medium">{u.name || '—'}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{u.email}</div>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-600">
                    {u.role || '—'}<br /><span className="text-[10px] text-slate-400">{u.department || '—'}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-col gap-1">
                      {companies.map(c => (
                        <Pill
                          key={c.id}
                          color={c.primary_color || '#1F7A6E'}
                          active={u.payroll_company_id === c.id}
                          onClick={() => setPayroll(u, c.id)}
                        >
                          {c.short_key || c.name}
                        </Pill>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1.5 items-center">
                      {companies.map(c => {
                        const has = (u.accessible_company_ids || []).includes(c.id);
                        const isPayroll = u.payroll_company_id === c.id;
                        return (
                          <button
                            key={c.id}
                            onClick={() => !isPayroll && toggleAccess(u, c.id)}
                            disabled={isPayroll || savingUserId === u.user_id}
                            data-testid={`access-toggle-${u.user_id}-${c.short_key || c.id}`}
                            title={isPayroll ? 'Always granted (payroll company)' : (has ? 'Click to remove' : 'Click to grant access')}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] uppercase tracking-wider font-display font-bold border transition-all ${
                              has ? 'shadow-sm' : 'opacity-40 hover:opacity-100'
                            } ${isPayroll ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                            style={has ? {
                              background: c.primary_color || '#1F7A6E',
                              color: 'white',
                              borderColor: c.primary_color || '#1F7A6E',
                            } : {
                              background: 'white',
                              color: c.primary_color || '#1F7A6E',
                              borderColor: c.primary_color || '#1F7A6E',
                            }}
                          >
                            {has ? <Check size={9} /> : <Plus size={9} />}
                            {c.short_key || c.name}
                          </button>
                        );
                      })}
                      {savingUserId === u.user_id && <Loader2 size={12} className="animate-spin text-[#1F7A6E] ml-1" />}
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400 text-sm">No users match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function TransferTab({ companies, onDone }) {
  const [employeeId, setEmployeeId] = useState('');
  const [fromCo, setFromCo] = useState('');
  const [toCo, setToCo] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [empOptions, setEmpOptions] = useState([]);
  const [empSearch, setEmpSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const h = await apiGet('/companies/transfers/history');
        setHistory(h);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (empSearch.length < 2) { setEmpOptions([]); return; }
      try {
        const r = await apiGet('/hr/employees', { q: empSearch, limit: 10 });
        const arr = Array.isArray(r) ? r : (r?.items || r?.employees || []);
        setEmpOptions(arr.slice(0, 10));
      } catch { setEmpOptions([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [empSearch]);

  const submit = async () => {
    if (!employeeId || !fromCo || !toCo) {
      toast.error('Pick employee, from-company and to-company');
      return;
    }
    if (fromCo === toCo) {
      toast.error('From and To companies cannot be the same');
      return;
    }
    setSubmitting(true);
    try {
      await apiPost('/companies/transfer-employee', {
        employee_id: employeeId, from_company_id: fromCo, to_company_id: toCo,
        effective_date: effectiveDate, reason,
      });
      toast.success('Employee transferred');
      setEmployeeId(''); setEmpSearch(''); setReason('');
      const h = await apiGet('/companies/transfers/history');
      setHistory(h);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Transfer failed');
    }
    setSubmitting(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="flex items-center gap-2 mb-4">
          <ArrowRightLeft size={15} className="text-[#1F7A6E]" />
          <h3 className="font-display font-bold text-sm">Transfer Employee Between Companies</h3>
        </div>
        <p className="text-xs text-slate-600 mb-4">
          Moves an employee's payroll + attendance going forward from one company to another. Historical records remain intact under the original company.
        </p>
        <div className="space-y-3">
          <div>
            <label className="kpi-overline mb-1 block">Employee</label>
            <input
              type="text"
              value={empSearch}
              onChange={e => { setEmpSearch(e.target.value); setEmployeeId(''); }}
              placeholder="Type name or emp_id (min 2 chars)…"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
              data-testid="transfer-emp-search"
            />
            {empOptions.length > 0 && !employeeId && (
              <div className="mt-1 border border-slate-200 rounded-sm max-h-48 overflow-auto bg-white shadow-md">
                {empOptions.map(e => (
                  <button
                    key={e.id}
                    onClick={() => {
                      setEmployeeId(e.id);
                      setEmpSearch(`${e.name} (${e.emp_id || ''})`);
                      setEmpOptions([]);
                      if (e.company_id) setFromCo(e.company_id);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-teal-50 text-xs"
                    data-testid={`emp-opt-${e.id}`}
                  >
                    <div className="font-medium">{e.name}</div>
                    <div className="text-[10px] text-slate-500">{e.emp_id} · {e.designation || '—'}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="kpi-overline mb-1 block">From Company</label>
              <select value={fromCo} onChange={e => setFromCo(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="transfer-from">
                <option value="">— Select —</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="kpi-overline mb-1 block">To Company</label>
              <select value={toCo} onChange={e => setToCo(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="transfer-to">
                <option value="">— Select —</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="kpi-overline mb-1 block">Effective Date</label>
            <input type="date" value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono" />
          </div>
          <div>
            <label className="kpi-overline mb-1 block">Reason (optional)</label>
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </div>
          <button
            onClick={submit}
            disabled={submitting || !employeeId}
            data-testid="transfer-submit-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs uppercase tracking-wider font-display font-bold rounded-sm disabled:opacity-50"
          >
            {submitting ? <Loader2 size={13} className="animate-spin" /> : <ArrowRightLeft size={13} />}
            Transfer Employee
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <h3 className="font-display font-bold text-sm mb-3">Recent Transfers</h3>
        <div className="space-y-2 max-h-[500px] overflow-auto">
          {history.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-6">No transfers yet.</p>
          ) : history.map(h => (
            <div key={h.id} className="border-l-4 border-[#1F7A6E] bg-slate-50 p-3 text-xs">
              <div className="font-medium text-slate-800">{h.employee_name}</div>
              <div className="text-[10px] text-slate-500 mt-1">
                {h.from_company_id?.slice(-6)} → {h.to_company_id?.slice(-6)} on {h.effective_date}
              </div>
              {h.reason && <div className="text-[10px] text-slate-600 mt-1 italic">"{h.reason}"</div>}
              <div className="text-[10px] text-slate-400 mt-1">by {h.transferred_by} · {(h.transferred_at || '').slice(0, 10)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
